import { computed, effect, inject, Injectable, Signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { ToastrSuccessComponent } from '@shared/components/toastr/toastr-success.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { Référentiel } from '../../administration/gestion-référentiels/models/référentiel.model';
import { TechniqueAnimation } from '../../administration/technique-animation/model/technique-animation.model';
import { Modalité } from '../../modalités/models/modalité.model';
import { FormControlNames, FormGroupNames } from '../../référentiel-métier-parcours/models/form-control-names.model';
import { Interrelation } from '../../référentiel-métier/référentiel-metier-interrelations/models/interrelation.model';
import { Aptitude } from '../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Production } from '../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/production.model';
import { AptitudeEvaluee } from '../models/aptitude-évaluée.model';
import { LieuType } from '../models/lieu-type.enum';
import { SupportActivité } from '../models/support-activité.model';
import { Sequence } from '../models/séquence.model';
import { ÉtatActivité } from '../models/état-activité.enum';
import { ActivitéStore } from '../store/activité.store';
import { ActiviteUpdateRequest } from './requests/update-request';
import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';

@Injectable({
  providedIn: 'root',
})
export class ActivitéFormService {
  readonly #fb = inject(FormBuilder);
  readonly #activitéStore = inject(ActivitéStore);
  readonly #référentielStore = inject(ReferentielStore);
  readonly #router = inject(Router);

  readonly activitéSélectionnée = computed(() => this.#activitéStore.selectedEntity());
  readonly toutesLesAptitudes: Signal<Aptitude[]> = computed(() =>
    this.aptitudePrincipaleAptitude.value
      ? [this.aptitudePrincipaleAptitude.value, ...this.aptitudesSecondaires.value]
      : [],
  );

  form: FormGroup = this.#fb.group({
    [FormGroupNames.INFORMATIONS_PRATIQUES]: this.#fb.group({
      [FormControlNames.NOM]: [null, { validators: [Validators.required], updateOn: 'blur' }],
      [FormControlNames.REFERENTIEL]: [null, [Validators.required]],
      [FormControlNames.MODALITE]: [null, [Validators.required]],
      [FormControlNames.TECHNIQUES_ANIMATION]: [[], [Validators.required]],
      [FormControlNames.LIEU]: [null, [Validators.required]],
      [FormControlNames.DUREE]: [null, [Validators.required]],
      [FormControlNames.DESCRIPTION]: [null, [Validators.required]],
    }),
    [FormGroupNames.APTITUDES]: this.#fb.group({
      [FormGroupNames.APTITUDE_PRINCIPALE]: this.#fb.group({
        [FormControlNames.APTITUDE]: [null, [Validators.required]],
        [FormControlNames.EST_EVALUEE]: false,
      }),
      [FormControlNames.APTITUDES_SECONDAIRES]: this.#fb.array([]),
    }),
    [FormControlNames.INTERRELATIONS]: this.#fb.control([]),
    [FormControlNames.PRODUCTIONS]: this.#fb.control([]),
    [FormControlNames.SUPPORTS]: this.#fb.array([]),
  });

  constructor() {
    effect(() => {
      const activite = this.activitéSélectionnée();
      if (activite) {
        const aptitudePrincipale = activite.aptitudesEvaluees.find((x) => x.estAptitudePrincipale);
        const aptitudesSecondaires = activite.aptitudesEvaluees.filter((x) => !x.estAptitudePrincipale);

        this.nom.setValue(activite.nom, { emitEvent: false });
        this.referentiel.setValue(activite.referentiel!, { emitEvent: false });
        this.duree.setValue(activite.duree, { emitEvent: false });
        this.modalite.setValue(activite.modalite, { emitEvent: false });
        this.lieu.setValue(activite.lieu, { emitEvent: false });
        this.description.setValue(activite.description, { emitEvent: false });
        this.aptitudesSecondaires.clear({ emitEvent: false });
        this.techniquesAnimation.setValue(activite.techniquesAnimation, { emitEvent: false });

        this.interrelations.setValue(activite.interrelations, { emitEvent: false });

        this.aptitudePrincipale.setValue(
          {
            aptitude: aptitudePrincipale?.aptitude ?? null,
            estEvaluee: aptitudePrincipale?.estEvaluee ?? false,
          },
          { emitEvent: false },
        );
        aptitudesSecondaires.forEach((x) => this.ajouterAptitudeSecondaire(x));

        this.productions.setValue(activite.productions, { emitEvent: false });

        this.supports.clear({ emitEvent: false });
        activite.supports.forEach((x) => this.ajouterSupport(x));

        if (activite.etat === ÉtatActivité.TERMINEE) this.form.disable({ emitEvent: false });
      } else {
        const currentReferentiel = this.#référentielStore.selectedEntity();

        this.form.reset(
          {
            [FormGroupNames.INFORMATIONS_PRATIQUES]: {
              [FormControlNames.NOM]: null,
              [FormControlNames.REFERENTIEL]: currentReferentiel,
              [FormControlNames.MODALITE]: null,
              [FormControlNames.TECHNIQUES_ANIMATION]: [],
              [FormControlNames.LIEU]: null,
              [FormControlNames.DUREE]: null,
              [FormControlNames.DESCRIPTION]: null,
            },
            [FormGroupNames.APTITUDES]: {
              [FormGroupNames.APTITUDE_PRINCIPALE]: {
                [FormControlNames.APTITUDE]: null,
                [FormControlNames.EST_EVALUEE]: false,
              },
              [FormControlNames.APTITUDES_SECONDAIRES]: [],
            },
            [FormControlNames.INTERRELATIONS]: [],
            [FormControlNames.PRODUCTIONS]: [],
            [FormControlNames.SUPPORTS]: [],
          },
          { emitEvent: false },
        );
        this.form.enable();
      }
    });
  }

  ajouterAptitudeSecondaire(initialValues?: { aptitude?: Aptitude; estEvaluee: boolean }) {
    const aptitudeForm = this.#fb.group({
      aptitude: [initialValues?.aptitude, [Validators.required]],
      estEvaluee: [initialValues?.estEvaluee ?? false],
    });

    this.aptitudesSecondaires.push(aptitudeForm, { emitEvent: false });
  }

  ajouterSupport(support?: SupportActivité): void {
    const supportForm = this.#fb.group({
      nom: [support?.nom],
      lien: [support?.lien],
      tags: [support?.tags],
    });

    this.supports.push(supportForm, { emitEvent: false });
  }

  supprimerAptitude(index: number) {
    this.aptitudesSecondaires.removeAt(index);
  }

  submit(sequence: Sequence) {
    if (!this.nom.value) return;

    const activité = this.activitéSélectionnée();
    const idActivité = activité?.id;

    const aptitudePrincipale: AptitudeEvaluee | null = this.aptitudePrincipale.value.aptitude
      ? {
          idAptitude: this.aptitudePrincipale.value.aptitude.id,
          aptitude: this.aptitudePrincipale.value.aptitude,
          estEvaluee: this.aptitudePrincipale.value.estEvaluee ?? false,
          estAptitudePrincipale: true,
        }
      : null;
    const aptitudesSecondaires: AptitudeEvaluee[] =
      this.aptitudesSecondaires.value?.map((x) => ({
        idAptitude: x.aptitude.id,
        aptitude: x.aptitude,
        estEvaluee: x.estEvaluee,
        estAptitudePrincipale: false,
      })) ?? [];

    const data = {
      nom: this.nom.value,
      duree: this.duree.value,
      idModalite: this.modalite.value?.id,
      modalite: this.modalite.value,
      lieu: this.lieu.value,
      description: this.description.value,
      aptitudesEvaluees: aptitudePrincipale ? [aptitudePrincipale, ...aptitudesSecondaires] : [],
      idReferentiel: this.referentiel.value?.id,
      idsInterrelations: this.interrelations.value?.map((x) => x.id),
      interrelations: this.interrelations.value,
      idsTechniquesAnimation: this.techniquesAnimation.value?.map((x) => x.id),
      techniquesAnimation: this.techniquesAnimation.value,
      idsProductions: this.productions.value?.map((x) => x.id),
      productions: this.productions.value,
      supports: this.supports.value,
    } satisfies ActiviteUpdateRequest;

    if (idActivité) {
      this.#activitéStore.modifier({
        id: idActivité,
        data: data,
        next: () => this.form.reset(undefined, { emitEvent: false }),
      });
    } else {
      this.#activitéStore.ajouter({
        nouvelleActivite: {
          ...data,
          idSequence: sequence.id,
        },
        next: (result) => {
          const segments = this.#router.url.split('/');
          segments.pop();
          segments.push('édition', result.id);
          this.#router.navigateByUrl(segments.join('/'), { replaceUrl: true });
        },
      });
    }
  }

  publier() {
    const toastrId = uuidv7();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message: 'Merci de renseigner tous les champs marqués comme requis.',
          id: toastrId,
        },
        id: toastrId,
      });
      return;
    }

    const activite = this.activitéSélectionnée();
    const idActivite = activite?.id;
    if (idActivite && activite) {
      this.#activitéStore.modifier({
        id: idActivite,
        data: {
          ...activite,
          etat: ÉtatActivité.TERMINEE,
        },
        next: () =>
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: "L'activité a bien été marquée comme Terminée.",
              id: toastrId,
            },
            id: toastrId,
          }),
      });
    }
  }

  get informationsPratiques() {
    return this.form.get(FormGroupNames.INFORMATIONS_PRATIQUES) as FormGroup;
  }

  get aptitudes() {
    return this.form.get(FormGroupNames.APTITUDES) as FormGroup;
  }

  // Informations pratiques
  get nom() {
    return this.informationsPratiques.get(FormControlNames.NOM) as FormControl<string>;
  }

  get referentiel() {
    return this.informationsPratiques.get(FormControlNames.REFERENTIEL) as FormControl<Référentiel>;
  }

  get modalite() {
    return this.informationsPratiques.get(FormControlNames.MODALITE) as FormControl<Modalité>;
  }

  get techniquesAnimation() {
    return this.informationsPratiques.get(FormControlNames.TECHNIQUES_ANIMATION) as FormControl<TechniqueAnimation[]>;
  }

  get lieu() {
    return this.informationsPratiques.get(FormControlNames.LIEU) as FormControl<LieuType>;
  }

  get duree() {
    return this.informationsPratiques.get(FormControlNames.DUREE) as FormControl<number>;
  }

  get description() {
    return this.informationsPratiques.get(FormControlNames.DESCRIPTION) as FormControl<string>;
  }

  // Aptitudes
  get aptitudePrincipale() {
    return this.aptitudes.get(FormGroupNames.APTITUDE_PRINCIPALE) as FormGroup<{
      [FormControlNames.APTITUDE]: FormControl<Aptitude | null>;
      [FormControlNames.EST_EVALUEE]: FormControl<boolean>;
    }>;
  }

  get aptitudePrincipaleAptitude() {
    return this.aptitudePrincipale.get(FormControlNames.APTITUDE) as FormControl<Aptitude | null>;
  }

  get aptitudePrincipaleEstEvaluee() {
    return this.aptitudePrincipale.get(FormControlNames.EST_EVALUEE) as FormControl<boolean>;
  }

  get aptitudesSecondaires() {
    return this.aptitudes.get(FormControlNames.APTITUDES_SECONDAIRES) as FormArray<FormGroup>;
  }

  // Interrelations
  get interrelations() {
    return this.form.get(FormControlNames.INTERRELATIONS) as FormControl<Interrelation[]>;
  }

  // Productions
  get productions() {
    return this.form.get(FormControlNames.PRODUCTIONS) as FormControl<Production[]>;
  }

  // Supports
  get supports() {
    return this.form.get(FormControlNames.SUPPORTS) as FormArray<FormGroup>;
  }
}
